import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Save, X, Image, Plus, AlertTriangle, CalendarIcon, Lock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  LIFECYCLE_STAGE_LABELS,
  LIFECYCLE_STAGE_BUILD_LABELS,
  LIFECYCLE_STAGE_BUILD_OPTIONS,
  LIFECYCLE_STAGE_TENDER_LABELS,
  LIFECYCLE_STAGE_TENDER_OPTIONS,
  LIFECYCLE_WATCH_SUBSTATUS_LABELS,
  LIFECYCLE_WATCH_SUBSTATUS_OPTIONS,
  type LifecycleStage,
  type LifecycleStageBuild,
  type LifecycleStageTender,
  type LifecycleWatchSubstatus,
} from '@/constants/lifecycleStages';
import { useCanOverrideLifecycle } from '@/hooks/maintenance/useCanOverrideLifecycle';
import { useUpdateLifecycleOverride } from '@/hooks/maintenance/useUpdateLifecycleOverride';
import { useUpdateLifecycleStageBuild } from '@/hooks/maintenance/useUpdateLifecycleStageBuild';
import { useUpdateLifecycleStageTender } from '@/hooks/maintenance/useUpdateLifecycleStageTender';
import { useUpdateWatchSubstatus } from '@/hooks/maintenance/useUpdateWatchSubstatus';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

// Parse Postgres interval (object like {months:12} or string like "12 mons" / "1 year 2 mons") to total months
const intervalToMonths = (v: any): number | null => {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'object') {
    const years = Number(v.years || 0);
    const months = Number(v.months || 0);
    const total = years * 12 + months;
    return total > 0 ? total : null;
  }
  if (typeof v === 'string') {
    let total = 0;
    const y = v.match(/(\d+)\s*year/);
    const m = v.match(/(\d+)\s*mon/);
    if (y) total += parseInt(y[1], 10) * 12;
    if (m) total += parseInt(m[1], 10);
    if (!y && !m) {
      const n = parseInt(v, 10);
      if (!isNaN(n)) total = n;
    }
    return total > 0 ? total : null;
  }
  return null;
};
import { supabase } from '@/integrations/supabase/client';
import { Project, ProjectStatus } from '@/types/project';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useRealtimeQuery } from '@/hooks/useRealtimeQuery';
import { useDialogStatePersisted } from '@/hooks/useDialogStatePersisted';

import { useMutationWithToast } from '@/hooks/useQueryWithToast';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAccessibleProjectTypes, useAccessibleProjectGroups } from '@/hooks/useAccessibleFilterOptions';
import { useAccessibleContractYears } from '@/hooks/useAccessibleContractYears';
import { useCustomGroups } from '@/hooks/useCustomGroups';
import { toast } from 'sonner';

interface ProjectEditDialogProps {
  projectId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectEditDialog = ({ projectId, isOpen, onClose }: ProjectEditDialogProps) => {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  
  // Fetch real-time project data
  const { data: project, isLoading: projectLoading, error: projectError } = useRealtimeQuery({
    queryKey: ['project-edit', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      
      const { data, error } = await supabase
        .from('projects')
        .select('id, project_name, status, project_type, project_group, contract_year, image_url, company, description, is_active, created_at, updated_at, handover_date, warranty_period, gross_floor_area_m2, total_manhour_estimate, lifecycle_stage_override, lifecycle_stage_build, lifecycle_stage_tender, watch_substatus')
        .eq('id', projectId)
        .single();
      
      if (error) throw error;
      return data;
    },
    tableName: 'projects',
    enabled: isOpen && !!projectId,
    enableToasts: false, // Disable toasts for edit dialog to avoid noise
    refetchOnMount: 'always', // Always refetch on dialog reopen to avoid stale form values
  });
  
  // Fetch project types and groups from database
  const { data: projectTypes = [], isLoading: projectTypesLoading } = useAccessibleProjectTypes();
  const { data: databaseGroups = [], isLoading: projectGroupsLoading } = useAccessibleProjectGroups();
  const { data: contractYears = [], isLoading: contractYearsLoading } = useAccessibleContractYears();
  
  // Use unified custom groups hook
  const { customGroups, addGroup, groupNameExists } = useCustomGroups();

  // Lifecycle override permission gate (FE; RLS trigger is source of truth)
  const canOverrideLifecycle = useCanOverrideLifecycle();
  const updateOverrideMutation = useUpdateLifecycleOverride();
  const updateBuildStageMutation = useUpdateLifecycleStageBuild();
  const updateTenderStageMutation = useUpdateLifecycleStageTender();
  const updateWatchSubstatusMutation = useUpdateWatchSubstatus();
  
  // SIMPLIFIED: Only project_name, status, project_type and image_url editable
  const [formData, setFormData] = useState({
    project_name: '',
    status: 'intake' as ProjectStatus,
    project_type: '',
    project_group: '',
    contract_year: '',
    image_url: '',
    company: '', // ENP or Solid
    handover_date: undefined as Date | undefined,
    warranty_period_months: '12',
    gross_floor_area_m2: '',
    total_manhour_estimate: '',
    lifecycle_stage_override: '' as '' | LifecycleStage,
    lifecycle_stage_build: '' as '' | LifecycleStageBuild,
    lifecycle_stage_tender: '' as '' | LifecycleStageTender,
    watch_substatus: '' as '' | LifecycleWatchSubstatus,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [hasChangedWhileEditing, setHasChangedWhileEditing] = useState(false);
  const [initialProjectData, setInitialProjectData] = useState<Project | null>(null);
  
  // New group creation state
  const [showNewGroupForm, setShowNewGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // Persist form draft values
  useEffect(() => {
    if (isOpen && projectId && formData.project_name) {
      localStorage.setItem(
        `project-edit-draft-${projectId}`,
        JSON.stringify({ ...formData, timestamp: Date.now() })
      );
    }
  }, [formData, isOpen, projectId]);

  // Reset form state when dialog closes so reopen re-hydrates from fresh project data
  useEffect(() => {
    if (!isOpen) {
      setInitialProjectData(null);
      setFormData({
        project_name: '',
        status: 'intake' as ProjectStatus,
        project_type: '',
        project_group: '',
        contract_year: '',
        image_url: '',
        company: '',
        handover_date: undefined as Date | undefined,
        warranty_period_months: '12',
        gross_floor_area_m2: '',
        total_manhour_estimate: '',
        lifecycle_stage_override: '' as '' | LifecycleStage,
        lifecycle_stage_build: '' as '' | LifecycleStageBuild,
        lifecycle_stage_tender: '' as '' | LifecycleStageTender,
        watch_substatus: '' as '' | LifecycleWatchSubstatus,
      });
      setImageFile(null);
      setImagePreview(null);
      setHasChangedWhileEditing(false);
      if (projectId) {
        queryClient.removeQueries({ queryKey: ['project-edit', projectId] });
        try {
          localStorage.removeItem(`project-edit-draft-${projectId}`);
        } catch (error) {
          // localStorage unavailable (Safari private mode)
        }
      }
    }
  }, [isOpen, projectId, queryClient]);

  // Clear draft helper
  const clearDraft = () => {
    if (projectId) {
      localStorage.removeItem(`project-edit-draft-${projectId}`);
    }
  };
  
  // Combine database groups and custom groups only
  const allGroups = [
    ...databaseGroups.map(name => ({ id: name, name, color: undefined })),
    ...customGroups
  ];

  // SIMPLIFIED: Only reset name, status, type and image
  useEffect(() => {
    if (!project) return;

      const wp = intervalToMonths((project as any).warranty_period);
      const projectData = {
        project_name: project.project_name || '',
        status: project.status || 'intake',
        project_type: project.project_type || '',
        project_group: project.project_group || 'none',
        contract_year: project.contract_year || 'none',
        image_url: project.image_url || '',
        company: (project as any).company || 'none',
        handover_date: (project as any).handover_date ? new Date((project as any).handover_date) : undefined,
        warranty_period_months: wp != null ? String(wp) : '12',
        gross_floor_area_m2: (project as any).gross_floor_area_m2 != null ? String((project as any).gross_floor_area_m2) : '',
        total_manhour_estimate: (project as any).total_manhour_estimate != null ? String((project as any).total_manhour_estimate) : '',
        lifecycle_stage_override: (((project as any).lifecycle_stage_override ?? '') as '' | LifecycleStage),
        lifecycle_stage_build: (((project as any).lifecycle_stage_build ?? '') as '' | LifecycleStageBuild),
        lifecycle_stage_tender: (((project as any).lifecycle_stage_tender ?? '') as '' | LifecycleStageTender),
        watch_substatus: (((project as any).watch_substatus ?? '') as '' | LifecycleWatchSubstatus),
      };
      
      // Check if this is the first load or if data changed while editing
      if (!initialProjectData) {
        // First load - check for draft
        const draft = localStorage.getItem(`project-edit-draft-${projectId}`);
        if (draft) {
          try {
            const parsed = JSON.parse(draft);
            const isExpired = Date.now() - parsed.timestamp > 24 * 60 * 60 * 1000; // 24 hours
            if (!isExpired) {
              if (parsed.handover_date) parsed.handover_date = new Date(parsed.handover_date);
              setFormData(parsed);
              setImagePreview(parsed.image_url || null);
              setInitialProjectData(project);
              setHasChangedWhileEditing(false);
              return; // Use draft
            }
          } catch (e) {
            // Invalid draft, ignore
          }
        }
        // No valid draft, use project data
        setInitialProjectData(project);
        setFormData(projectData);
        setImageFile(null);
        setImagePreview(project.image_url || null);
        setHasChangedWhileEditing(false);
      } else {
        // Check if project was updated by someone else
        const hasExternalChanges = 
          initialProjectData.updated_at !== project.updated_at ||
          initialProjectData.project_name !== project.project_name ||
          initialProjectData.status !== project.status ||
          initialProjectData.project_type !== project.project_type ||
          initialProjectData.project_group !== project.project_group ||
          ((initialProjectData.contract_year === 'none' ? null : (initialProjectData.contract_year || null)) !== (project.contract_year || null)) ||
          initialProjectData.image_url !== project.image_url;
          
        if (hasExternalChanges) {
          setHasChangedWhileEditing(true);
        }
      }
  }, [project, initialProjectData, projectId]);

  const updateProjectMutation = useMutationWithToast({
    mutationFn: async (updates: Partial<Project>) => {
      if (!project) throw new Error('No project selected');
      
      let finalUpdates = { ...updates };
      
      // If there's a new image file, upload it first
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        
        const { data: uploadResponse, error: uploadError } = await supabase.functions.invoke('upload-project-image', {
          body: formData
        });
        
        if (uploadError) {
          throw new Error(`Failed to upload image: ${uploadError.message}`);
        }
        
        finalUpdates.image_url = uploadResponse.imageUrl;
      }
      
      
      const { data, error } = await supabase
        .from('projects')
        .update(finalUpdates)
        .eq('id', project.id)
        .select()
        .single();
      
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      
      queryClient.invalidateQueries({
        queryKey: ['projects', 'status-stats'],
        exact: false,
        refetchType: 'active',
      });

      queryClient.refetchQueries({ queryKey: ['global-project-access'], type: 'all' });

      // Invalidate the dialog's own project cache so reopen shows fresh values
      if (project?.id) {
        queryClient.invalidateQueries({ queryKey: ['project-edit', project.id] });
      }

      // Chain: if override changed AND user has permission, persist separately
      const currentOverride = (((project as any)?.lifecycle_stage_override) ?? null) as LifecycleStage | null;
      const newOverride: LifecycleStage | null =
        formData.lifecycle_stage_override === '' ? null : formData.lifecycle_stage_override;
      const currentBuildStage = (((project as any)?.lifecycle_stage_build) ?? null) as LifecycleStageBuild | null;
      const newBuildStage: LifecycleStageBuild | null =
        formData.lifecycle_stage_build === '' ? null : formData.lifecycle_stage_build;
      const currentTenderStage = (((project as any)?.lifecycle_stage_tender) ?? null) as LifecycleStageTender | null;
      const newTenderStage: LifecycleStageTender | null =
        formData.lifecycle_stage_tender === '' ? null : formData.lifecycle_stage_tender;
      const currentSubstatus = (((project as any)?.watch_substatus) ?? null) as LifecycleWatchSubstatus | null;
      const newSubstatus: LifecycleWatchSubstatus | null =
        formData.watch_substatus === '' ? null : formData.watch_substatus;
      const isWatchStage = formData.lifecycle_stage_override === 'watch';

      if (
        project &&
        formData.status === 'service' &&
        canOverrideLifecycle &&
        newOverride !== currentOverride
      ) {
        updateOverrideMutation.mutate(
          { projectId: project.id, override: newOverride },
          {
            onSuccess: () => {
              clearDraft();
              onClose();
            },
            // On error: toast already fired in hook; keep dialog open for retry
          }
        );
      } else if (
        project &&
        formData.status === 'build' &&
        canOverrideLifecycle &&
        newBuildStage !== currentBuildStage
      ) {
        updateBuildStageMutation.mutate(
          { projectId: project.id, stage: newBuildStage },
          {
            onSuccess: () => {
              clearDraft();
              onClose();
            },
          }
        );
      } else if (
        project &&
        formData.status === 'tender' &&
        canOverrideLifecycle &&
        newTenderStage !== currentTenderStage
      ) {
        updateTenderStageMutation.mutate(
          { projectId: project.id, stage: newTenderStage },
          {
            onSuccess: () => {
              clearDraft();
              onClose();
            },
          }
        );
      } else if (
        project &&
        formData.status === 'service' &&
        isWatchStage &&
        canOverrideLifecycle &&
        newSubstatus !== currentSubstatus &&
        newSubstatus !== null
      ) {
        updateWatchSubstatusMutation.mutate(
          { projectId: project.id, value: newSubstatus },
          {
            onSuccess: () => {
              clearDraft();
              onClose();
            },
          }
        );
      } else {
        clearDraft();
        onClose();
      }
    },
    onError: (error) => {
      
    },
    successMessage: "Đã cập nhật dự án",
    errorMessage: "Không thể cập nhật dự án",
    invalidateQueries: [['projects'], ['user-projects'], ['project-detail'], ['projects-optimized-v2'], ['global-project-access']]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.project_name.trim()) {
      return;
    }

    // SIMPLIFIED: Only update name, status, type, company and image
    const updates: Record<string, any> = {
      project_name: formData.project_name.trim(),
      status: formData.status,
      project_type: formData.project_type,
      project_group: formData.project_group === 'none' ? null : formData.project_group,
      contract_year: formData.contract_year === 'none' ? null : formData.contract_year,
      image_url: formData.image_url.trim() || null,
      company: formData.company === 'none' ? null : formData.company
    };

    // Thông số dự án — luôn lưu (mọi status)
    const gfaRaw = formData.gross_floor_area_m2.trim();
    let gfa: number | null = null;
    if (gfaRaw !== '') {
      const n = Number(gfaRaw);
      if (isNaN(n) || n < 0) {
        toast.error('Diện tích sàn phải là số ≥ 0');
        return;
      }
      gfa = n;
    }
    updates.gross_floor_area_m2 = gfa;

    // Lifecycle Settings — only when status === 'service'
    if (formData.status === 'service') {
      const monthsRaw = formData.warranty_period_months.trim();
      const months = parseInt(monthsRaw, 10);
      if (!monthsRaw || isNaN(months) || months <= 0 || !Number.isInteger(Number(monthsRaw))) {
        toast.error('Thời hạn bảo hành phải là số nguyên dương');
        return;
      }

      const mhRaw = formData.total_manhour_estimate.trim();
      let mh: number | null = null;
      if (mhRaw !== '') {
        const n = Number(mhRaw);
        if (isNaN(n) || n < 0) {
          toast.error('Tổng manhour phải là số ≥ 0');
          return;
        }
        mh = n;
      }

      updates.handover_date = formData.handover_date ? format(formData.handover_date, 'yyyy-MM-dd') : null;
      updates.warranty_period = `${months} months`;
      updates.total_manhour_estimate = mh;
    }

    updateProjectMutation.mutate(updates);
  };

  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImageFile(null);
      setImagePreview(formData.image_url || null);
    }
  };

  const handleAddGroup = () => {
    const trimmedName = newGroupName.trim();
    
    if (!trimmedName) {
      toast.error("Tên nhóm không được để trống");
      return;
    }
    
    // Check for duplicate names (including database groups)
    if (groupNameExists(trimmedName) || databaseGroups.includes(trimmedName)) {
      toast.error("Tên nhóm đã tồn tại");
      return;
    }
    
    // Add new custom group using hook
    const newGroup = addGroup({
      name: trimmedName,
      description: 'Nhóm tùy chỉnh',
      color: '#6366f1'
    });
    
    if (newGroup) {
      setFormData(prev => ({ ...prev, project_group: trimmedName }));
      setNewGroupName('');
      setShowNewGroupForm(false);
      toast.success(`Đã thêm nhóm "${trimmedName}"`);
    } else {
      toast.error("Không thể thêm nhóm mới");
    }
  };

  if (!isOpen || !projectId) return null;
  
  if (projectLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`w-[95vw] max-w-3xl mx-auto max-h-[90vh] overflow-y-auto ${isMobile ? 'p-4' : ''}`}>
          <DialogHeader>
            <DialogTitle>Đang tải...</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (projectError || !project) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`w-[95vw] max-w-3xl mx-auto max-h-[90vh] overflow-y-auto ${isMobile ? 'p-4' : ''}`}>
          <DialogHeader>
            <DialogTitle>Lỗi</DialogTitle>
          </DialogHeader>
          <div className="p-4 text-center text-muted-foreground">
            {projectError ? 'Không thể tải thông tin dự án' : 'Dự án không tồn tại'}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`w-[95vw] max-w-3xl mx-auto max-h-[90vh] overflow-y-auto ${isMobile ? 'p-4' : ''}`}>
        <DialogHeader>
          <DialogTitle>Chỉnh sửa dự án</DialogTitle>
          <DialogDescription>
            Chỉnh sửa thông tin dự án bao gồm tên, trạng thái, loại dự án và hình ảnh thumbnail
          </DialogDescription>
        </DialogHeader>

        {hasChangedWhileEditing && (
          <Alert className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Dự án này đã được cập nhật bởi người khác. Các thay đổi của bạn có thể ghi đè lên dữ liệu mới.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className={`${isMobile ? 'space-y-3' : 'space-y-4'}`}>
          <div>
            <Label htmlFor="project_name">Tên dự án *</Label>
            <Input
              id="project_name"
              value={formData.project_name}
              onChange={handleChange('project_name')}
              placeholder="Nhập tên dự án"
              className="mt-1"
              required
            />
            {formData.project_type && (
              <div className="mt-1 text-sm text-muted-foreground">
                Loại: {formData.project_type}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="status">Trạng thái</Label>
            <Select
              value={formData.status}
              onValueChange={(value: ProjectStatus) => 
                setFormData(prev => ({ ...prev, status: value }))
              }
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="intake">Tiếp nhận</SelectItem>
                <SelectItem value="tender">Đấu thầu</SelectItem>
                <SelectItem value="build">Thi công</SelectItem>
                <SelectItem value="defects">Sửa chữa</SelectItem>
                <SelectItem value="archive">Lưu trữ</SelectItem>
                <SelectItem value="service">Bảo hành</SelectItem>
                <SelectItem value="inactive">Ngừng hoạt động</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="project_type">Loại dự án</Label>
            <Select
              value={formData.project_type}
              onValueChange={(value: string) => 
                setFormData(prev => ({ ...prev, project_type: value }))
              }
              disabled={projectTypesLoading}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={projectTypesLoading ? "Đang tải..." : "Chọn loại dự án"} />
              </SelectTrigger>
              <SelectContent>
                {projectTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="project_group">Nhóm dự án</Label>
            <div className="space-y-2">
              <Select
                value={formData.project_group}
                onValueChange={(value: string) => 
                  setFormData(prev => ({ ...prev, project_group: value }))
                }
                disabled={projectGroupsLoading}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={projectGroupsLoading ? "Đang tải..." : "Chọn nhóm dự án"} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="none">Không gán nhóm</SelectItem>
                  {allGroups.map((group) => (
                    <SelectItem key={group.id} value={group.name}>
                      <div className="flex items-center gap-2">
                        {group.color && (
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: group.color }}
                          />
                        )}
                        <span>{group.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {!showNewGroupForm ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewGroupForm(true)}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Tạo nhóm mới
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder="Tên nhóm mới"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddGroup();
                      }
                      if (e.key === 'Escape') {
                        setShowNewGroupForm(false);
                        setNewGroupName('');
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddGroup}
                    disabled={!newGroupName.trim()}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowNewGroupForm(false);
                      setNewGroupName('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
             </div>
           </div>

          <div>
            <Label htmlFor="contract_year">Năm hợp đồng</Label>
            <Select
              value={formData.contract_year}
              onValueChange={(value: string) => 
                setFormData(prev => ({ ...prev, contract_year: value }))
              }
              disabled={contractYearsLoading}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={contractYearsLoading ? "Đang tải..." : "Chọn năm hợp đồng"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Không gán năm</SelectItem>
                {contractYears.map((year) => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>


          <div>
            <Label htmlFor="company">Công ty</Label>
            <Select
              value={formData.company}
              onValueChange={(value: string) => 
                setFormData(prev => ({ ...prev, company: value }))
              }
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Chọn công ty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Không chọn</SelectItem>
                <SelectItem value="ENP">ENP</SelectItem>
                <SelectItem value="Solid">Solid</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="image_file">Hình ảnh thumbnail</Label>
            <div className="space-y-2 mt-1">
              <div className="flex items-center gap-4">
                <Input
                  id="image_file"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="flex-1"
                />
                {imagePreview && (
                  <div className={`${isMobile ? 'w-12 h-12' : 'w-16 h-16'} border rounded-lg overflow-hidden flex-shrink-0`}>
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                {!imagePreview && (
                  <div className={`${isMobile ? 'w-12 h-12' : 'w-16 h-16'} border border-dashed rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Image className={`${isMobile ? 'h-4 w-4' : 'h-6 w-6'} text-muted-foreground`} />
                  </div>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Chọn file mới để thay đổi hình ảnh. Chỉ chấp nhận JPG, PNG, WebP. Tối đa 5MB.
              </p>
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <h3 className="text-base font-medium">Thông số dự án</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="gross_floor_area_m2">Diện tích sàn (m²)</Label>
                <Input
                  id="gross_floor_area_m2"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="vd 1000"
                  value={formData.gross_floor_area_m2}
                  onChange={(e) => {
                    
                    setFormData(prev => ({ ...prev, gross_floor_area_m2: e.target.value }));
                  }}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {formData.status === 'service' && (
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-semibold">Lifecycle Settings</h3>

              <div>
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="lifecycle_stage_override">Trạng thái</Label>
                  {formData.lifecycle_stage_override !== '' && (
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-label="Đang đè thủ công" />
                  )}
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        tabIndex={0}
                        className={cn(
                          'mt-1 block w-full',
                          !canOverrideLifecycle && 'cursor-not-allowed'
                        )}
                      >
                        <Select
                          value={formData.lifecycle_stage_override === '' ? '__auto__' : formData.lifecycle_stage_override}
                          onValueChange={(v) =>
                            setFormData(prev => ({
                              ...prev,
                              lifecycle_stage_override: v === '__auto__' ? '' : (v as LifecycleStage),
                            }))
                          }
                          disabled={!canOverrideLifecycle}
                        >
                          <SelectTrigger id="lifecycle_stage_override" className="w-full">
                            <SelectValue placeholder="Tự động" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__auto__">Tự động</SelectItem>
                            <SelectItem value="yet_transferred">{LIFECYCLE_STAGE_LABELS.yet_transferred}</SelectItem>
                            <SelectItem value="warranty">{LIFECYCLE_STAGE_LABELS.warranty}</SelectItem>
                            <SelectItem value="watch">{LIFECYCLE_STAGE_LABELS.watch}</SelectItem>
                            <SelectItem value="maintenance">{LIFECYCLE_STAGE_LABELS.maintenance}</SelectItem>
                          </SelectContent>
                        </Select>
                      </span>
                    </TooltipTrigger>
                    {!canOverrideLifecycle && (
                      <TooltipContent>Chỉ PM/SOL/PA được sửa</TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                {formData.lifecycle_stage_override !== '' && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Đang đè thủ công, sẽ không tự cập nhật theo dòng thời gian
                  </p>
                )}
              </div>

              {formData.lifecycle_stage_override === 'watch' && (
                <div>
                  <Label htmlFor="watch_substatus">Trạng thái theo dõi</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          tabIndex={0}
                          className={cn(
                            'mt-1 block w-full',
                            !canOverrideLifecycle && 'cursor-not-allowed'
                          )}
                        >
                          <Select
                            value={formData.watch_substatus === '' ? 'theo_doi' : formData.watch_substatus}
                            onValueChange={(v) =>
                              setFormData(prev => ({
                                ...prev,
                                watch_substatus: v as LifecycleWatchSubstatus,
                              }))
                            }
                            disabled={!canOverrideLifecycle}
                          >
                            <SelectTrigger id="watch_substatus" className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {LIFECYCLE_WATCH_SUBSTATUS_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {LIFECYCLE_WATCH_SUBSTATUS_LABELS[opt.value as LifecycleWatchSubstatus]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </span>
                      </TooltipTrigger>
                      {!canOverrideLifecycle && (
                        <TooltipContent>Chỉ PM/SOL/PA được sửa</TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}

              <div>
                <Label>Ngày bàn giao</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        'mt-1 w-full justify-start text-left font-normal',
                        !formData.handover_date && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.handover_date
                        ? format(formData.handover_date, 'dd/MM/yyyy')
                        : 'Chọn ngày'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.handover_date}
                      onSelect={(d) => setFormData(prev => ({ ...prev, handover_date: d ?? undefined }))}
                      initialFocus
                      className={cn('p-3 pointer-events-auto')}
                    />
                  </PopoverContent>
                </Popover>
                {formData.handover_date && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-1 h-auto p-1 text-xs text-muted-foreground"
                    onClick={() => setFormData(prev => ({ ...prev, handover_date: undefined }))}
                  >
                    Xoá ngày
                  </Button>
                )}
              </div>

              <div>
                <Label htmlFor="warranty_period_months">Thời hạn bảo hành (tháng) *</Label>
                <Input
                  id="warranty_period_months"
                  type="number"
                  min="1"
                  step="1"
                  value={formData.warranty_period_months}
                  onChange={(e) => setFormData(prev => ({ ...prev, warranty_period_months: e.target.value }))}
                  className="mt-1"
                  required
                />
              </div>

              <div>
                <Label htmlFor="total_manhour_estimate">Tổng manhour ước tính</Label>
                <Input
                  id="total_manhour_estimate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.total_manhour_estimate}
                  onChange={(e) => setFormData(prev => ({ ...prev, total_manhour_estimate: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          {formData.status === 'build' && (
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-semibold">Build Lifecycle Stage</h3>
              <div>
                <Label htmlFor="lifecycle_stage_build">Trạng thái</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        tabIndex={0}
                        className={cn(
                          'mt-1 block w-full',
                          !canOverrideLifecycle && 'cursor-not-allowed'
                        )}
                      >
                        <Select
                          value={formData.lifecycle_stage_build === '' ? '__none__' : formData.lifecycle_stage_build}
                          onValueChange={(v) =>
                            setFormData(prev => ({
                              ...prev,
                              lifecycle_stage_build: v === '__none__' ? '' : (v as LifecycleStageBuild),
                            }))
                          }
                          disabled={!canOverrideLifecycle}
                        >
                          <SelectTrigger id="lifecycle_stage_build" className="w-full">
                            <SelectValue placeholder="Chưa set" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Chưa set</SelectItem>
                            {LIFECYCLE_STAGE_BUILD_OPTIONS.filter(o => o.value !== null).map(o => (
                              <SelectItem key={o.value as string} value={o.value as string}>
                                {LIFECYCLE_STAGE_BUILD_LABELS[o.value as LifecycleStageBuild]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </span>
                    </TooltipTrigger>
                    {!canOverrideLifecycle && (
                      <TooltipContent>Chỉ PM/SOL/PA được sửa</TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                {formData.lifecycle_stage_build !== '' && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Trạng thái build hiện tại — cập nhật thủ công theo tiến độ thi công.
                  </p>
                )}
              </div>
            </div>
          )}

          {formData.status === 'tender' && (
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-semibold">Tender Lifecycle Stage</h3>
              <div>
                <Label htmlFor="lifecycle_stage_tender">Trạng thái</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        tabIndex={0}
                        className={cn(
                          'mt-1 block w-full',
                          !canOverrideLifecycle && 'cursor-not-allowed'
                        )}
                      >
                        <Select
                          value={formData.lifecycle_stage_tender === '' ? '__none__' : formData.lifecycle_stage_tender}
                          onValueChange={(v) =>
                            setFormData(prev => ({
                              ...prev,
                              lifecycle_stage_tender: v === '__none__' ? '' : (v as LifecycleStageTender),
                            }))
                          }
                          disabled={!canOverrideLifecycle}
                        >
                          <SelectTrigger id="lifecycle_stage_tender" className="w-full">
                            <SelectValue placeholder="Chưa set" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Chưa set</SelectItem>
                            {LIFECYCLE_STAGE_TENDER_OPTIONS.filter(o => o.value !== null).map(o => (
                              <SelectItem key={o.value as string} value={o.value as string}>
                                {LIFECYCLE_STAGE_TENDER_LABELS[o.value as LifecycleStageTender]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </span>
                    </TooltipTrigger>
                    {!canOverrideLifecycle && (
                      <TooltipContent>Chỉ PM/SOL/PA được sửa</TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                {formData.lifecycle_stage_tender !== '' && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Trạng thái tender hiện tại — cập nhật thủ công theo tiến độ đấu thầu.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className={`flex ${isMobile ? 'flex-col' : 'flex-col sm:flex-row'} gap-2 pt-4`}>
            <Button
              type="submit"
              disabled={updateProjectMutation.isPending || updateOverrideMutation.isPending || updateBuildStageMutation.isPending || updateTenderStageMutation.isPending || updateWatchSubstatusMutation.isPending}
              className={`${isMobile ? 'min-h-[44px] order-1' : 'flex-1'}`}
            >
              <Save className="h-4 w-4 mr-2" />
              {(updateProjectMutation.isPending || updateOverrideMutation.isPending || updateBuildStageMutation.isPending || updateTenderStageMutation.isPending || updateWatchSubstatusMutation.isPending) ? 'Đang lưu...' : 'Lưu'}
            </Button>
            <Button 
              type="button"
              variant="outline" 
              onClick={() => {
                clearDraft();
                onClose();
              }}
              disabled={updateProjectMutation.isPending}
              className={`${isMobile ? 'min-h-[44px] order-2' : 'flex-1 sm:flex-none'}`}
            >
              <X className="h-4 w-4 mr-2" />
              Hủy
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectEditDialog;